import { Document, Page, View } from "@react-pdf/renderer";
import React from "react";
import { IntlProvider } from "react-intl";
import {
  CellTextBoxNoBorder,
  Row,
  Table,
  TextCell,
  safeString,
  styles,
} from "./reportingHelpers";

const startDateOptions = {
  weekday: "short",
  year: "numeric",
  month: "short",
  day: "numeric",
};
const startTimeOptions = { hour: "numeric", minute: "numeric" };

export const ScoresheetReport = (props) => {
  const { locale, intl } = props;
  const {
    teams,
    fixture,
    roles,
    fixtureProfile: { profile: fixtureProfile },
  } = props.data;
  const gameStartDate = Intl.DateTimeFormat(undefined, startDateOptions).format(
    new Date(fixture.startTimeLocal)
  );
  const gameStartTime = Intl.DateTimeFormat(undefined, startTimeOptions).format(
    new Date(fixture.startTimeLocal)
  );
  const { homeTeam, awayTeam } = teams;

  const homeTeamPlayers = Object.values(homeTeam.persons || {});
  const awayTeamPlayers = Object.values(awayTeam.persons || {});

  if (homeTeamPlayers && awayTeamPlayers) {
    homeTeamPlayers.sort((a, b) => a.bib - b.bib);
    awayTeamPlayers.sort((a, b) => a.bib - b.bib);
  }

  return (
    <IntlProvider locale={locale ?? "en-US"}>
      <Document>
        <Page size="A4" style={styles.page}>
          <View>
            <Header
              awayTeamName={awayTeam.name}
              competition={fixture.competitionName}
              fixtureNumber={fixture.fixtureNumber}
              gameStartDate={gameStartDate}
              gameStartTime={gameStartTime}
              homeTeamName={homeTeam.name}
              intl={intl}
              roles={roles}
              venueName={fixture.venueName}
            />
            <View
              style={{ flexDirection: "row", justifyContent: "space-between" }}
            >
              <View style={{ width: "49%" }}>
                <ScoresheetTeamHeader
                  teamName={homeTeam.name}
                  teamSide="A"
                  overtimeLabel={intl.formatMessage({
                    id: "reports.header.overtimelabel",
                  })}
                  intl={intl}
                />
                <ScoresheetTeamTable
                  players={homeTeamPlayers}
                  intl={intl}
                  roles={roles.filter((r) => r.entityId === homeTeam.entityId)}
                  fixtureProfile={fixtureProfile}
                />
                <ScoresheetTeamHeader
                  teamName={awayTeam.name}
                  teamSide="B"
                  overtimeLabel={intl.formatMessage({
                    id: "reports.header.overtimelabel",
                  })}
                  intl={intl}
                />
                <ScoresheetTeamTable
                  players={awayTeamPlayers}
                  intl={intl}
                  roles={roles.filter((r) => r.entityId === awayTeam.entityId)}
                  fixtureProfile={fixtureProfile}
                />
              </View>
              <View style={{ width: "50%" }}>
                <RunningScoreTable intl={intl} />
                <ScoresByPeriods intl={intl} />
              </View>
            </View>
            <Footer intl={intl} roles={roles} />
          </View>
        </Page>
      </Document>
    </IntlProvider>
  );
};

const ScoresheetPlayerRow = ({ player, intl, fixtureProfile }) => {
  const playerName = player
    ? `${safeString(player, "name")}${
        player.captain
          ? ` ${intl.formatMessage({
              id: "reports.boxscore.table.captain",
              defaultMessage: "(C)",
            })}`
          : ""
      }`
    : " ";
  return (
    <View
      style={{
        height: "4mm",
        fontSize: "6pt",
        display: "flex",
        flexDirection: "row",
        borderTopWidth: 1,
      }}
    >
      <TextCell style={{ width: "8mm" }}> </TextCell>
      <TextCell
        style={{ width: "54mm", textAlign: "left", paddingLeft: "2pt" }}
      >
        {playerName}
      </TextCell>
      <TextCell style={{ width: "8mm" }}>
        {player ? safeString(player, "bib") : " "}
      </TextCell>
      <TextCell style={{ width: "8mm" }}> </TextCell>
      <View style={{ width: "20mm", display: "flex", flexDirection: "row" }}>
        {Array(fixtureProfile.maxFoulsPersonal)
          .fill(null)
          .map((_el, index) => (
            <TextCell key={`team_player_foul_${index}`} />
          ))}
      </View>
    </View>
  );
};

const ScoresheetTeamHeaderQuarterRow = ({ quarterNumber }) => (
  <View
    style={{
      display: "flex",
      flexDirection: "row",
      marginLeft: "4pt",
      height: "4mm",
    }}
  >
    <CellTextBoxNoBorder
      style={{
        fontSize: "7pt",
        backgroundColor: "black",
        color: "white",
        height: "4mm",
        width: "4mm",
      }}
    >
      {quarterNumber}
    </CellTextBoxNoBorder>
    <View
      style={{
        display: "flex",
        flexDirection: "row",
        width: "20mm",
        borderWidth: 1,
        borderRightWidth: 0,
        marginLeft: "4pt",
      }}
    >
      <TextCell> </TextCell>
      <TextCell> </TextCell>
      <TextCell> </TextCell>
      <TextCell> </TextCell>
    </View>
  </View>
);

const ScoresheetTeamHeader = ({ teamName, overtimeLabel, intl, teamSide }) => (
  <View style={{ display: "flex", flexDirection: "column" }}>
    <CellTextBoxNoBorder
      style={{ fontSize: "8pt", textAlign: "left", width: "100%" }}
    >
      {`${intl.formatMessage({ id: "reports.scoresheet.team" })} ${teamSide}: ${teamName}`}
    </CellTextBoxNoBorder>
    <View style={{ display: "flex", flexDirection: "row" }}>
      <View style={{ width: "23mm", display: "flex", flexDirection: "column" }}>
        <CellTextBoxNoBorder
          style={{ fontSize: "8pt", textAlign: "left", width: "100%" }}
        >
          {intl.formatMessage({ id: "reports.scoresheet.timeouts" })}
        </CellTextBoxNoBorder>
        <View style={{ display: "flex", flexDirection: "row", height: "5mm", }}>
          <CellTextBoxNoBorder style={{ width: "5mm", textAlign: "left" }}>
            {intl.formatMessage({ id: "reports.header.half1" })}
          </CellTextBoxNoBorder>
          <TextCell
            style={{
              width: "5mm",
              borderTopWidth: 1,
              borderLeftWidth: 1,
            }}
          > </TextCell>
          <TextCell style={{ width: "5mm", borderTopWidth: 1 }}> </TextCell>
        </View>
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            height: "5mm",
          }}
        >
          <CellTextBoxNoBorder style={{ width: "5mm", textAlign: "left" }}>
            {intl.formatMessage({ id: "reports.header.half2" })}
          </CellTextBoxNoBorder>
          <TextCell
            style={{
              width: "5mm",
              borderTopWidth: 1,
              borderLeftWidth: 1,
            }}
          > </TextCell>
          <TextCell style={{ width: "5mm", borderTopWidth: 1 }}> </TextCell>
          <TextCell style={{ width: "5mm", borderTopWidth: 1 }}> </TextCell>
        </View>
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            height: "5mm",
          }}
        >
          <CellTextBoxNoBorder style={{ width: "5mm", textAlign: "left" }}>
            {intl.formatMessage({ id: "reports.header.overtimelabel" })}
          </CellTextBoxNoBorder>
          <TextCell
            style={{
              width: "5mm",
              borderTopWidth: 1,
              borderBottomWidth: 1,
              borderLeftWidth: 1,
            }}
          >
            {" "}
          </TextCell>
          <TextCell
            style={{ width: "5mm", borderTopWidth: 1, borderBottomWidth: 1 }}
          >
            {" "}
          </TextCell>
          <TextCell
            style={{ width: "5mm", borderTopWidth: 1, borderBottomWidth: 1 }}
          >
            {" "}
          </TextCell>
        </View>
      </View>
      <View>
        <CellTextBoxNoBorder
          style={{ fontSize: "8pt", textAlign: "left", width: "100%" }}
        >
          {intl.formatMessage({ id: "reports.scoresheet.teamfouls" })}
        </CellTextBoxNoBorder>
        <View style={{ display: "flex", flexDirection: "row" }}>
          <CellTextBoxNoBorder style={{ width: "1cm", textAlign: "left" }}>
            {intl.formatMessage({ id: "reports.scoresheet.quarter" })}:
          </CellTextBoxNoBorder>
          <ScoresheetTeamHeaderQuarterRow quarterNumber={1} />
          <ScoresheetTeamHeaderQuarterRow quarterNumber={2} />
        </View>
        <View style={{ display: "flex", flexDirection: "row" }}>
          <CellTextBoxNoBorder style={{ width: "1cm", textAlign: "left" }}>
            {intl.formatMessage({ id: "reports.scoresheet.quarter" })}:
          </CellTextBoxNoBorder>
          <ScoresheetTeamHeaderQuarterRow quarterNumber={3} />
          <ScoresheetTeamHeaderQuarterRow quarterNumber={4} />
        </View>
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <CellTextBoxNoBorder
            style={{ fontSize: "7pt", textAlign: "left", width: "2.4cm" }}
          >
            {overtimeLabel}
          </CellTextBoxNoBorder>
          <View
            style={{
              display: "flex",
              flexDirection: "row",
            }}
          >
            <CellTextBoxNoBorder
              style={{
                fontSize: "7pt",
                textAlign: "right",
                marginRight: "2pt",
              }}
            >
              {intl.formatMessage({ id: "reports.scoresheet.hcc" })}
            </CellTextBoxNoBorder>
            <Row style={{ borderWidth: 1, borderRightWidth: 0 }} width="10mm">
              <TextCell> </TextCell>
              <TextCell> </TextCell>
            </Row>
          </View>
        </View>
      </View>
    </View>
  </View>
);

const ScoresheetTeamTableHeader = ({ intl, fixtureProfile }) => (
  <View
    style={{
      height: "10mm",
      fontSize: "6pt",
      display: "flex",
      flexDirection: "row",
      borderRightWidth: 1,
    }}
  >
    <TextCell style={{ width: "8mm" }}>
      {intl.formatMessage({ id: "reports.scoresheet.table.licenseno" })}
    </TextCell>
    <TextCell style={{ width: "54mm", textAlign: "center" }}>
      {intl.formatMessage({ id: "reports.scoresheet.table.players" })}
    </TextCell>
    <TextCell style={{ width: "8mm" }}>
      {intl.formatMessage({ id: "reports.scoresheet.table.no" })}
    </TextCell>
    <TextCell style={{ width: "8mm" }}>
      {intl.formatMessage({ id: "reports.scoresheet.table.playerin" })}
    </TextCell>
    <View style={{ width: "20mm", display: "flex", flexDirection: "column" }}>
      <CellTextBoxNoBorder style={{ textAlign: "center", width: "100%" }}>
        {intl.formatMessage({ id: "reports.scoresheet.table.fouls" })}
      </CellTextBoxNoBorder>
      <View style={{ display: "flex", flexDirection: "row" }}>
        {Array(fixtureProfile.maxFoulsPersonal)
          .fill(null)
          .map((_el, index) => (
            <CellTextBoxNoBorder key={`team_header_foul_${index}`}>
              {index + 1}
            </CellTextBoxNoBorder>
          ))}
      </View>
    </View>
  </View>
);

const ScoresheetTeamTable = ({ players, intl, roles, fixtureProfile }) => {
  const playersList = Array(15).fill(null);
  players.forEach((player, index) => {
    playersList[index] = player;
  });
  const coachName = roles.find((r) => r.role === "COACH")?.name;
  const coachAssistantsNames = roles
    .filter((r) => r.role === "COACH_ASSISTANT")
    .sort((a, b) => (a.number ?? 0) - (b.number ?? 0))
    .map((r) => r.name)
    .join(", ");

  return (
    <View
      style={{
        fontSize: "7pt",
        marginTop: "4pt",
        borderLeftWidth: 1,
        borderTopWidth: 1,
        borderBottomWidth: 1,
      }}
    >
      <ScoresheetTeamTableHeader intl={intl} fixtureProfile={fixtureProfile} />
      {players &&
        playersList.map((player, index) => (
          <ScoresheetPlayerRow
            player={player}
            key={index}
            intl={intl}
            fixtureProfile={fixtureProfile}
          />
        ))}
      <View
        style={{
          height: "4mm",
          fontSize: "6pt",
          display: "flex",
          flexDirection: "row",
          borderTopWidth: 1,
        }}
      >
        <TextCell
          style={{ width: "100%", textAlign: "left", paddingLeft: "2pt" }}
        >
          {`${intl.formatMessage({
            id: "reports.scoresheet.table.coach",
          })}: ${coachName}`}
        </TextCell>
        <View style={{ width: "13mm", display: "flex", flexDirection: "row" }}>
          <TextCell> </TextCell>
          <TextCell> </TextCell>
          <TextCell> </TextCell>
        </View>
      </View>
      <View
        style={{
          height: "4mm",
          fontSize: "6pt",
          display: "flex",
          flexDirection: "row",
          borderTopWidth: 1,
        }}
      >
        <TextCell
          style={{ width: "100%", textAlign: "left", paddingLeft: "2pt" }}
        >
          {`${intl.formatMessage({
            id: "reports.scoresheet.table.coachassistant",
          })}: ${coachAssistantsNames}`}
        </TextCell>
        <View style={{ width: "13mm", display: "flex", flexDirection: "row" }}>
          <TextCell> </TextCell>
          <TextCell> </TextCell>
          <TextCell> </TextCell>
        </View>
      </View>
    </View>
  );
};

const RunningScoreTableColumn = ({ startValue, intl }) => {
  return (
    <View>
      <View
        style={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          fontSize: "6pt",
          height: "4mm",
        }}
      >
        <TextCell style={{ width: "14mm", textAlign: "center" }}>
          {intl.formatMessage({ id: "reports.scoresheet.a" })}
        </TextCell>
        <TextCell style={{ width: "14mm" }}>
          {intl.formatMessage({ id: "reports.scoresheet.b" })}
        </TextCell>
      </View>
      {[...new Array(40)].map((value, index) => (
        <View
          key={startValue + index}
          style={{
            height: "4mm",
            fontSize: "6pt",
            display: "flex",
            flexDirection: "row",
            borderTopWidth: 1,
          }}
        >
          <TextCell> </TextCell>
          <TextCell>{startValue + index}</TextCell>
          <TextCell>{startValue + index}</TextCell>
          <TextCell> </TextCell>
        </View>
      ))}
    </View>
  );
};

const RunningScoreTable = ({ intl }) => (
  <View
    style={{
      flexDirection: "column",
      borderLeftWidth: 1,
      borderTopWidth: 1,
      borderBottomWidth: 1,
    }}
  >
    <TextCell
      style={{
        width: "100%",
        height: "5mm",
        fontSize: "8pt",
        borderBottomWidth: 1,
        borderRightWidth: 1,
      }}
    >
      {intl.formatMessage({ id: "reports.scoresheet.runningscore" })}
    </TextCell>
    <View style={{ display: "flex", flexDirection: "row" }}>
      <RunningScoreTableColumn startValue={1} intl={intl} />
      <RunningScoreTableColumn startValue={41} intl={intl} />
      <RunningScoreTableColumn startValue={81} intl={intl} />
      <RunningScoreTableColumn startValue={121} intl={intl} />
    </View>
  </View>
);

const Footer = ({ intl, roles }) => (
  <View style={{ display: "flex", flexDirection: "column", fontSize: "6pt" }}>
    <View
      style={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "space-between",
        borderWidth: 1,
        padding: "4pt",
        marginTop: "10pt",
      }}
    >
      <View style={{ display: "flex", flexDirection: "row", width: "48%" }}>
        <CellTextBoxNoBorder
          style={{ textAlign: "left", marginRight: "2pt", width: "20mm" }}
        >
          {intl.formatMessage({ id: "reports.scoresheet.scorer" })}
        </CellTextBoxNoBorder>
        <TextCell
          style={{
            borderBottomWidth: 1,
            borderRightWidth: 0,
            textAlign: "left",
            flex: 1,
          }}
        >
          {roles.find((role) => role.role === "SCORER")?.name}
        </TextCell>
      </View>
      <View style={{ display: "flex", flexDirection: "row", width: "49%" }}>
        <CellTextBoxNoBorder
          style={{ textAlign: "left", marginRight: "2pt", width: "23mm" }}
        >
          {intl.formatMessage({ id: "reports.scoresheet.operatorShotClock" })}
        </CellTextBoxNoBorder>
        <TextCell
          style={{
            borderBottomWidth: 1,
            borderRightWidth: 0,
            textAlign: "left",
            flex: 1,
          }}
        ></TextCell>
      </View>
      <View style={{ display: "flex", flexDirection: "row", width: "48%" }}>
        <CellTextBoxNoBorder
          style={{ textAlign: "left", marginRight: "2pt", width: "20mm" }}
        >
          {intl.formatMessage({ id: "reports.scoresheet.scorerassistant" })}
        </CellTextBoxNoBorder>
        <TextCell
          style={{
            borderBottomWidth: 1,
            borderRightWidth: 0,
            textAlign: "left",
            flex: 1,
          }}
        >
          {roles.find((role) => role.role === "SCORER_ASSISTANT")?.name}
        </TextCell>
      </View>
      <View style={{ display: "flex", flexDirection: "row", width: "49%" }}>
        <CellTextBoxNoBorder
          style={{ textAlign: "left", marginRight: "2pt", width: "23mm" }}
        >
          {intl.formatMessage({ id: "reports.scoresheet.crewchief" })}
        </CellTextBoxNoBorder>
        <TextCell
          style={{
            borderBottomWidth: 1,
            borderRightWidth: 0,
            textAlign: "left",
            flex: 1,
          }}
        ></TextCell>
      </View>
      <View style={{ display: "flex", flexDirection: "row", width: "48%" }}>
        <CellTextBoxNoBorder
          style={{ textAlign: "left", marginRight: "2pt", width: "20mm" }}
        >
          {intl.formatMessage({ id: "reports.scoresheet.timer" })}
        </CellTextBoxNoBorder>
        <TextCell
          style={{
            borderBottomWidth: 1,
            borderRightWidth: 0,
            textAlign: "left",
            flex: 1,
          }}
        >
          {roles.find((role) => role.role === "TIMER")?.name}
        </TextCell>
      </View>
      <View style={{ display: "flex", flexDirection: "row", width: "49%" }}>
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            width: "48%",
            marginRight: "11pt",
          }}
        >
          <CellTextBoxNoBorder
            style={{ textAlign: "left", marginRight: "2pt", width: "23mm" }}
          >
            {intl.formatMessage({ id: "reports.scoresheet.umpire1" })}
          </CellTextBoxNoBorder>
          <TextCell
            style={{
              borderBottomWidth: 1,
              borderRightWidth: 0,
              textAlign: "left",
              flex: 1,
            }}
          ></TextCell>
        </View>
        <View style={{ display: "flex", flexDirection: "row", width: "48%" }}>
          <CellTextBoxNoBorder
            style={{
              textAlign: "left",
              marginRight: "2pt",
              width: "12mm",
            }}
          >
            {intl.formatMessage({ id: "reports.scoresheet.umpire2" })}
          </CellTextBoxNoBorder>
          <TextCell
            style={{
              borderBottomWidth: 1,
              borderRightWidth: 0,
              textAlign: "left",
              flex: 1,
            }}
          ></TextCell>
        </View>
      </View>
    </View>
    <View style={{ display: "flex", flexDirection: "row", marginTop: "12pt" }}>
      <CellTextBoxNoBorder
        style={{ width: "50mm", textAlign: "left", alignItems: "flex-end" }}
      >
        {intl.formatMessage({ id: "reports.scoresheet.captainssignature" })}
      </CellTextBoxNoBorder>
      <TextCell
        style={{ flex: 1, borderBottomWidth: 1, borderRightWidth: 0 }}
      ></TextCell>
    </View>
  </View>
);

const ScoresByPeriodRow = ({ labelText = "Quarter" }) => (
  <View
    style={{
      width: "100%",
      height: "6mm",
      display: "flex",
      flexDirection: "row",
    }}
  >
    <CellTextBoxNoBorder style={{
      width: "24mm",
      paddingLeft: "6mm",
      textAlign: "left",
    }}>
      {labelText}
    </CellTextBoxNoBorder>
    <CellTextBoxNoBorder style={{ width: "4mm" }}>A</CellTextBoxNoBorder>
    <TextCell
      width="10mm"
      style={{ borderBottomWidth: 1, borderRightWidth: 0 }}
    >
      {" "}
    </TextCell>
    <CellTextBoxNoBorder style={{ width: "10mm" }}> </CellTextBoxNoBorder>
    <CellTextBoxNoBorder style={{ width: "4mm" }}>B</CellTextBoxNoBorder>
    <TextCell
      width="10mm"
      style={{ borderBottomWidth: 1, borderRightWidth: 0 }}
    >
      {" "}
    </TextCell>
    <CellTextBoxNoBorder style={{ width: "10mm" }}> </CellTextBoxNoBorder>
  </View>
);

const ScoresByPeriods = ({ intl }) => (
  <View style={{ display: "flex", flexDirection: "column" }}>
    <View style={{ display: "flex", flexDirection: "row" }}>
      <CellTextBoxNoBorder
        style={{ marginTop: "2mm", marginLeft: "2mm", width: "15mm" }}
      >
        {intl.formatMessage({ id: "reports.scoresheet.scores" })}
      </CellTextBoxNoBorder>
      <View>
        <ScoresByPeriodRow
          labelText={`${intl.formatMessage({
            id: "reports.scoresheet.quarter",
          })} 1`}
        />
        <ScoresByPeriodRow
          labelText={`${intl.formatMessage({
            id: "reports.scoresheet.quarter",
          })} 2`}
        />
        <ScoresByPeriodRow
          labelText={`${intl.formatMessage({
            id: "reports.scoresheet.quarter",
          })} 3`}
        />
        <ScoresByPeriodRow
          labelText={`${intl.formatMessage({
            id: "reports.scoresheet.quarter",
          })} 4`}
        />
        <ScoresByPeriodRow
          labelText={intl.formatMessage({ id: "reports.scoresheet.overtimes" })}
        />
      </View>
    </View>
    <View
      style={{
        height: "6mm",
        marginTop: "1mm",
        display: "flex",
        flexDirection: "row",
      }}
    >
      <CellTextBoxNoBorder style={{ width: "24mm", textAlign: "left" }}>
        {intl.formatMessage({ id: "reports.scoresheet.finalscore" })}
      </CellTextBoxNoBorder>
      <CellTextBoxNoBorder style={{ width: "14mm", textAlign: "right" }}>
        {intl.formatMessage({ id: "reports.scoresheet.teama" })}
      </CellTextBoxNoBorder>
      <TextCell
        width="10mm"
        style={{ borderBottomWidth: 1, borderRightWidth: 0 }}
      >
        {" "}
      </TextCell>
      <CellTextBoxNoBorder style={{ width: "14mm", textAlign: "right" }}>
        {intl.formatMessage({ id: "reports.scoresheet.teamb" })}
      </CellTextBoxNoBorder>
      <TextCell
        width="10mm"
        style={{ borderBottomWidth: 1, borderRightWidth: 0 }}
      >
        {" "}
      </TextCell>
    </View>
    <View
      style={{
        height: "7mm",
        display: "flex",
        flexDirection: "row",
      }}
    >
      <CellTextBoxNoBorder style={{ width: "27mm", textAlign: "left" }}>
        {intl.formatMessage({ id: "reports.scoresheet.winningteam" })}
      </CellTextBoxNoBorder>
      <TextCell
        width="40mm"
        style={{ borderBottomWidth: 1, borderRightWidth: 0, marginLeft: "5mm" }}
      >
        {" "}
      </TextCell>
    </View>
  </View>
);

const Header = ({
  awayTeamName,
  competition,
  fixtureNumber,
  gameStartDate,
  gameStartTime,
  homeTeamName,
  intl,
  roles,
  venueName,
}) => (
  <React.Fragment>
    <TextCell
      style={{
        width: "100%",
        border: "none",
        fontSize: "12pt",
        fontWeight: "bold",
      }}
    >
      {intl.formatMessage({
        id: "reports.scoresheet",
        defaultMessage: "Scoresheet",
      })}
    </TextCell>
    <View
      style={{
        display: "flex",
        flexDirection: "row",
        width: "100%",
        fontSize: "8pt",
      }}
    >
      <TextCell style={{ border: "none", width: "50%" }}>
        {`${intl.formatMessage({
          id: "reports.scoresheet.teama",
        })} ${homeTeamName}`}
      </TextCell>
      <TextCell style={{ border: "none", width: "50%" }}>
        {`${intl.formatMessage({
          id: "reports.scoresheet.teamb",
        })} ${awayTeamName}`}
      </TextCell>
    </View>
    <View style={{ marginBottom: "1mm" }}>
      <Table style={{ borderWidth: 1, padding: "1mm" }}>
        <Row borderless height="5mm" style={{ borderWidth: 0 }}>
          <TextCell width="20mm" left borderless>
            {intl.formatMessage({ id: "reports.scoresheet.competition" })}
          </TextCell>
          <TextCell width="52mm" left borderless style={{ fontWeight: "bold" }}>
            {competition}
          </TextCell>
          <TextCell width="7mm" left borderless>
            {intl.formatMessage({ id: "reports.scoresheet.date" })}
          </TextCell>
          <TextCell
            width="30mm"
            right
            borderless
            style={{ fontWeight: "bold" }}
          >
            {gameStartDate}
          </TextCell>
          <TextCell width="15mm" borderless>
            {intl.formatMessage({ id: "reports.scoresheet.time" })}
          </TextCell>
          <TextCell width="20mm" borderless style={{ fontWeight: "bold" }}>
            {gameStartTime}
          </TextCell>
          <TextCell width="45mm" borderless>
            {intl.formatMessage({ id: "reports.scoresheet.crewchief" })}{" "}
            <TextCell style={{ fontWeight: "bold" }}>
              {roles.find((role) => role.role === "CREW_CHIEF")?.name}
            </TextCell>
          </TextCell>
        </Row>
        <Row borderless height="5mm" style={{ borderWidth: 0 }}>
          <TextCell width="15mm" left borderless>
            {intl.formatMessage({ id: "reports.scoresheet.gameno" })}
          </TextCell>
          <TextCell width="27mm" left borderless style={{ fontWeight: "bold" }}>
            {fixtureNumber ?? " "}
          </TextCell>
          <TextCell width="10mm" left borderless>
            {intl.formatMessage({ id: "reports.scoresheet.place" })}
          </TextCell>
          <TextCell width="27mm" left borderless style={{ fontWeight: "bold" }}>
            {venueName}{" "}
          </TextCell>
          <TextCell width="22.5mm" borderless>
            {intl.formatMessage({ id: "reports.scoresheet.umpire" })}
          </TextCell>
          <TextCell width="35mm" borderless style={{ fontWeight: "bold" }}>
            {roles.find((role) => role.role === "UMPIRE")?.name}
          </TextCell>
          <TextCell width="22.5mm" borderless>
            {intl.formatMessage({ id: "reports.scoresheet.umpire1" })}
          </TextCell>
          <TextCell width="35mm" borderless style={{ fontWeight: "bold" }}>
            {roles.find((role) => role.role === "UMPIRE_1")?.name}
          </TextCell>
          <TextCell width="22.5mm" borderless>
            {intl.formatMessage({ id: "reports.scoresheet.umpire2" })}
          </TextCell>
          <TextCell width="35mm" borderless style={{ fontWeight: "bold" }}>
            {roles.find((role) => role.role === "UMPIRE_2")?.name}
          </TextCell>
        </Row>
      </Table>
    </View>
  </React.Fragment>
);
